import { useQuery } from '@tanstack/react-query';
import { auditoriaApi } from '../api/auditoria';
import type { AuditoriaFiltros } from '../types/auditoria';

const STALE_DROPDOWN = 5 * 60 * 1000; // 5 min — tipos/usuários/entidades distintos mudam pouco

/**
 * `enabled: false` (via `opcoes`) pausa a query sem limpar o cache — usado
 * pela aba Eventos quando `q` está preenchido com período inválido/incompleto:
 * o backend devolveria 400, então o front simplesmente não dispara a request
 * (ver `periodoServeParaBusca`) em vez de deixar o 400 vazar como erro genérico.
 */
export function useAuditoria(filtros: AuditoriaFiltros, opcoes?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['auditoria', filtros],
    queryFn: () => auditoriaApi.listar(filtros),
    enabled: opcoes?.enabled ?? true,
    // Lista paginada — re-busca quando filtros mudam (queryKey muda).
    // staleTime curto pra não exibir dados velhos depois de uma ação que gera audit.
    staleTime: 10 * 1000,
  });
}

export function useAuditoriaTipos() {
  return useQuery({
    queryKey: ['auditoria', 'tipos'],
    queryFn: () => auditoriaApi.tipos(),
    staleTime: STALE_DROPDOWN,
  });
}

export function useAuditoriaUsuariosDistintos() {
  return useQuery({
    queryKey: ['auditoria', 'usuarios-distintos'],
    queryFn: () => auditoriaApi.usuariosDistintos(),
    staleTime: STALE_DROPDOWN,
  });
}

export function useAuditoriaEntidades() {
  return useQuery({
    queryKey: ['auditoria', 'entidades'],
    queryFn: () => auditoriaApi.entidades(),
    staleTime: STALE_DROPDOWN,
  });
}

/**
 * Timeline de UMA entidade (aba Timeline). `enabled` só dispara com os dois
 * preenchidos — o backend responde 400 sem eles, e a aba trata isso como
 * "escolha entidade + id", não como erro de rede.
 */
export function useAuditoriaTimeline(entidade: string, entidadeId: string, limite = 200) {
  const habilitado = entidade.trim().length > 0 && entidadeId.trim().length > 0;
  return useQuery({
    queryKey: ['auditoria', 'timeline', entidade, entidadeId, limite],
    queryFn: () => auditoriaApi.timeline({ entidade, entidadeId, limite }),
    enabled: habilitado,
    staleTime: 10 * 1000,
  });
}

/** Agregados da aba Números — substitui os antigos KPIs stub (endpoint próprio). */
export function useAuditoriaNumeros(dias: number, incluirAdmin = false) {
  return useQuery({
    queryKey: ['auditoria', 'numeros', dias, incluirAdmin],
    queryFn: () => auditoriaApi.numeros(dias, incluirAdmin),
    staleTime: 60 * 1000,
  });
}
